import { useQueryClient } from '@tanstack/react-query'

import { useAppMutation } from '@/helpers/api/mutation'
import { createInstance } from '@/helpers/api/base'
import { useGetUserInfo } from '@/modules/Auth'
import { useModal } from '@/components/Modal'
import { useNotification } from '@/ui/Notification'
import { Log, LogUnfinishedMetadata } from '@/types/logs'
import { GET_FLAGGED_LOGS_KEY } from '@/pages/UnfinishedRecords/api/useGetFlaggedLogs'

interface ReopenRequest { logId: string; comment?: string }

export const useFlagRecordReopen = () => {
    const queryClient = useQueryClient()
    const { data: userInfo } = useGetUserInfo()
    const { closeModal } = useModal()
    const { showNotification } = useNotification()

    const { mutate, isPending } = useAppMutation<LogUnfinishedMetadata, Error, ReopenRequest>({
        mutationFn: ({ logId, comment }) =>
            createInstance({
                url: `/controller-logs/${logId}/flag-reopen`,
                method: 'PATCH',
                data: {
                    author: userInfo?.lastSessionDetails.authorName ?? '',
                    comment,
                },
            }),
        alertOnError: true,
        onSuccess: (data, { logId }) => {
            const updater = (prev: Log[] | undefined) =>
                prev?.map((log) => (log.id === logId ? { ...log, unfinishedFlagMetadata: data } : log))
            queryClient.setQueriesData<Log[]>({ queryKey: [GET_FLAGGED_LOGS_KEY] }, updater)
            queryClient.setQueriesData<Log[]>({ queryKey: ['controllerLogs'] }, updater)
            closeModal()
            showNotification({ message: 'Record reopened' })
        },
    })

    return { mutate, isPending }
}
